<template>
    <div v-if="hasContent" class="advisor-grounded">
        <template v-if="g.kind === 'value'">
            <div class="advisor-grounded__value">
                {{ g.number ?? '' }} <span class="advisor-grounded__unit">{{ g.unit ?? '' }}</span>
            </div>
            <div v-if="g.label" class="advisor-grounded__note">{{ g.label }}</div>
        </template>

        <p v-else-if="g.kind === 'help' && g.answer" class="advisor-grounded__help">{{ g.answer }}</p>

        <ul v-else-if="g.kind === 'list' && g.rows?.length" class="advisor-grounded__list">
            <li v-for="(row, index) in g.rows" :key="index" class="advisor-grounded__row">
                <span class="advisor-grounded__row-main">
                    <span class="advisor-grounded__row-label">{{ rowMain(row) }}</span>
                    <span v-if="rowSub(row)" class="advisor-grounded__row-sub">{{ rowSub(row) }}</span>
                </span>
                <span v-if="rowValue(row)" class="advisor-grounded__row-val">{{ rowValue(row) }}</span>
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { Grounded } from '@src/Advisor/Pages/types';

const props = defineProps<{ g: Grounded }>();

// empty/blocked entries carry no renderable body — the reply text explains them.
const hasContent = computed(
    () =>
        props.g.kind === 'value' ||
        (props.g.kind === 'help' && !!props.g.answer) ||
        (props.g.kind === 'list' && !!props.g.rows?.length),
);

const formatDate = (value: string): string => {
    const d = new Date(`${value}T00:00:00`);

    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' });
};

const rowMain = (row: Record<string, unknown>): string => {
    const date = typeof row.date === 'string' ? formatDate(row.date) : row.date;

    return String(row.message ?? row.employee ?? date ?? '');
};

const rowSub = (row: Record<string, unknown>): string => {
    const date = typeof row.date === 'string' ? formatDate(row.date) : '';

    return String(row.workplace ?? (row.message ? date : '') ?? '');
};

const rowValue = (row: Record<string, unknown>): string => {
    if (row.time) {
        return String(row.time);
    }

    for (const key of ['net_hours', 'remaining_hours', 'overtime_hours']) {
        if (row[key] !== undefined) {
            return `${row[key]} h`;
        }
    }

    return '';
};
</script>

<style scoped>
.advisor-grounded {
    margin-top: 0.5rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid rgb(0 0 0 / 0.08);
    border-radius: 0.5rem;
    background: rgb(0 0 0 / 0.03);
}

.advisor-grounded__value {
    font-size: 1.25rem;
    font-weight: 600;
}

.advisor-grounded__unit {
    font-size: 0.875rem;
    font-weight: 400;
    opacity: 0.7;
}

.advisor-grounded__note,
.advisor-grounded__row-sub {
    font-size: 0.75rem;
    opacity: 0.7;
}

.advisor-grounded__list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.advisor-grounded__row {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    align-items: baseline;
}

.advisor-grounded__row-main {
    display: flex;
    flex-direction: column;
}

.advisor-grounded__row-val {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}
</style>
